import { isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectorRef,
  ElementRef,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  ViewChild
} from "@angular/core";
import { AdManagerService } from "@core/services/ad-manager.service";
import { DeviceService } from "@core/services/device.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { fadeInOut } from "@shared/animations";

@Component({
  selector: "astrobin-ad-manager",
  template: `
    <div
      *ngIf="isBrowser && unitPath && divId && size"
      [class.ad-rendered]="rendered"
      [id]="divId"
      [style.height.px]="height"
      class="ad-container"
    ></div>

    <div *ngIf="loading && !rendered" class="loading-text">
      {{ "Loading ad..." | translate }}
    </div>

    <img
      *ngIf="!loading && !rendered && !!configName"
      @fadeInOut
      [alt]="'Thank you for not blocking ads!' | translate"
      [src]="'/assets/images/ads/' + configName + '/thank-you-for-not-blocking-ads.jpeg?v=1'"
      class="default-ad"
    />

    <button *ngIf="rendered" (click)="removeAds()" class="btn btn-link btn-no-block remove-ads">
      {{ "Remove ads" | translate }}
    </button>

    <ng-template #removeAdsOffcanvasTemplate let-offcanvas>
      <div class="offcanvas-header">
        <h5 class="offcanvas-title">
          {{ "Remove ads" | translate }}
        </h5>
        <button (click)="offcanvas.close()" class="btn-close" type="button"></button>
      </div>

      <div class="offcanvas-body">
        <p translate="We know, we're all used to the fact that ads on the Internet are annoying."></p>
        <p translate="However, please let us offer you a good reason to allow ads on AstroBin:"></p>
        <p
          class="alert alert-dark my-4"
          translate="There are only astronomy ads on AstroBin, that are published after careful review and approval. Nothing fishy, nothing off-topic, and nothing that will use any of your private information."
        ></p>

        <p
          translate="If you are on AstroBin Premium or AstroBin Ultimate, you can remove ads in your settings. If you are not, please consider supporting AstroBin by subscribing!"
        ></p>

        <p translate="Thank you!"></p>
      </div>
    </ng-template>
  `,
  styleUrls: ["./ad-manager.component.scss"],
  animations: [fadeInOut],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdManagerComponent implements OnChanges, OnDestroy {
  @Input() configName: string;

  @Output() adDisplayed = new EventEmitter<void>();

  @ViewChild("removeAdsOffcanvasTemplate") removeAdsOffcanvasTemplate: TemplateRef<any>;

  protected readonly isBrowser: boolean;
  protected unitPath: string;
  protected divId: string;
  protected size: any[];
  protected height: number;
  protected rendered = false;
  protected loading = true;

  private readonly _adSizes = {
    rectangular: {
      width: 920,
      height: 640
    },
    wide: {
      width: 2280,
      height: 280
    }
  };

  private _resizeObserver: ResizeObserver;

  constructor(
    public readonly adManagerService: AdManagerService,
    public readonly offcanvasService: NgbOffcanvas,
    public readonly deviceService: DeviceService,
    @Inject(PLATFORM_ID) platformId: Object,
    public readonly elementRef: ElementRef,
    public readonly utilsService: UtilsService,
    public readonly changeDetectorRef: ChangeDetectorRef
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser && "ResizeObserver" in window) {
      this._resizeObserver = new ResizeObserver(() => {
        this._calculateHeight();
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  ngOnDestroy(): void {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (this.isBrowser) {
      // Only proceed if configName actually changed or this is the first change
      const configNameChanged =
        changes.configName &&
        (changes.configName.firstChange || changes.configName.previousValue !== changes.configName.currentValue);

      if (!configNameChanged) {
        return;
      }

      const config = this.adManagerService.getAdConfig(this.configName);
      if (config) {
        this.unitPath = config.adUnitPath;
        this.divId = config.divId;
        this.size = config.adSize;

        // Calculate height immediately to prevent layout shift
        this._calculateHeight();

        // Start observing parent container for size changes
        if (this._resizeObserver && this.elementRef.nativeElement.parentElement) {
          this._resizeObserver.observe(this.elementRef.nativeElement.parentElement);
        }

        // First destroy any existing slot
        await this.adManagerService.destroyAdSlot(this.divId);

        this._defineAndDisplayAd();
      } else {
        this.unitPath = null;
        this.divId = null;
        this.size = null;
        this.height = 0;
      }
    }
  }

  private _defineAndDisplayAd(): void {
    if (!this.unitPath || !this.divId || !this.size) {
      return;
    }

    const hasSlot = this.adManagerService.hasAdSlot(this.divId);

    if (hasSlot) {
      void this.adManagerService
        .refreshAd(this.divId)
        .then(displayed => {
          this._onAdResult(displayed);
          this.changeDetectorRef.markForCheck();
        })
        .catch(error => {
          // Handle rejection (e.g., "Too many requests")
          this.loading = false;
          this.changeDetectorRef.markForCheck();
        });
    } else {
      void this.adManagerService
        .defineAdSlot(this.configName, this.unitPath, this.size, this.divId)
        .then(() => {
          this.loading = true;
          this.changeDetectorRef.markForCheck();

          void this.adManagerService.displayAd(this.divId).then(displayed => {
            this._onAdResult(displayed);
            this.changeDetectorRef.markForCheck();
          });
        })
        .catch(error => {
          console.error(`[AdManagerComponent] _defineAndDisplayAd - defineAdSlot rejected:`, error);
        });
    }
  }

  refreshAd(): void {
    if (this.loading) {
      return;
    }

    this.loading = true;

    this._defineAndDisplayAd();
  }

  removeAds(): void {
    void this.offcanvasService.open(this.removeAdsOffcanvasTemplate, {
      position: this.deviceService.offcanvasPosition()
    });
  }

  destroyAd(): Promise<void> {
    return this.adManagerService.destroyAdSlot(this.divId);
  }

  private _calculateHeight(): void {
    if (!this.configName || !this._adSizes[this.configName]) {
      this.height = 0;
      return;
    }

    const containerWidth = this.elementRef.nativeElement.parentElement?.offsetWidth || 0;
    const adSize = this._adSizes[this.configName];

    // Calculate the ad height by scaling proportionally to the container width
    this.height = containerWidth > 0 ? (adSize.height * containerWidth) / adSize.width : adSize.height;
  }

  private _onAdResult(displayed: boolean): void {
    if (displayed) {
      this.utilsService.delay(1).subscribe(() => {
        requestAnimationFrame(() => {
          this.rendered = displayed;
          this.adDisplayed.emit();
          this.loading = false;
          this.changeDetectorRef.markForCheck();
        });
      });
    } else {
      this.rendered = false;
      this.loading = false;
      this.changeDetectorRef.markForCheck();
    }
  }
}
